import React, { isValidElement, useCallback, useEffect } from 'react';

import { connect, useDispatch } from 'react-redux';

import PropTypes from 'prop-types';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';

import { sendClick } from 'reaxl-analytics';
import { InventoryListingOverlayActions } from 'reaxl-listing';

import { saveVDPListing } from '@/utilities/saveListing';

import {
    authDuck,
    compareListingsDuck,
    savedInventoryDuck,
    userDuck,
} from '@/ducks';

import {
    vdpResultsDuck,
} from '@/ducks/vdp';

import ShareContentContainer from '@/containers/ShareContentContainer';

export function SaveAndShareContainer({
    listing,
    isSaved,
    isLoggedIn,
    children,
}) {

    const {
        share: [isShareEnabled],
    } = useFeatures([
        'share',
    ]);

    const dispatch = useDispatch();

    // retrieve saved inventory ids and set them in the inventory reducer
    const initializeUserInfo = useCallback(() => {
        dispatch(userDuck.creators.hydrateUserInventory({ isLoggedIn, onLoggedInStateReady: () => { } }));
    }, [dispatch, isLoggedIn]);

    const { brands, isBrand } = useBrand();
    const isKbbBranded = isBrand(brands.KBB_BRAND);

    listing.birfParams = {
        co_txt_url: 'save',
        c: listing?.owner?.id,
        v: listing.id,
        par: 'v_lstg',
    };

    useEffect(() => {
        // Only call this function on KBB
        if (isKbbBranded) {
            initializeUserInfo(); // Hydrate saved inventory
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = useCallback((event) => {
        dispatch(saveVDPListing({
            event,
            listing,
            wasSaved: isSaved,
            sendClick,
        }));
    }, [dispatch, listing, isSaved]);

    return isValidElement(children) && (
        <div
            data-cmp="saveAndShareOverlay"
            className="display-flex flex-column justify-content-center"
        >
            <div
                className="overlay-actions-container"
                style={{ height: '0', zIndex: '1' }}
            >
                <InventoryListingOverlayActions
                    listing={listing}
                    onSaveToggle={handleSave}
                    isSaved={isSaved}
                    additionalCta={isShareEnabled && (
                        <ShareContentContainer
                            key="share-glyph"
                            glyphColor="accent"
                            uiContext="glyph"
                            className="display-flex margin-right-4"
                        />
                    )}
                />
            </div>
            {children}
        </div>
    );
}

const mapStateToProps = (state) => {
    const isLoggedIn = authDuck.selectors.isLoggedIn(state);
    const listing = vdpResultsDuck.selectors.getVdpActiveInventory(state);
    const listingIdsToCompare = compareListingsDuck.selectors.getActiveListingIds(state) || [];
    const savedListingIds = savedInventoryDuck.selectors.getSavedInventoryIds(state) || [];

    return {
        listing,
        listingIdsToCompare,
        isSaved: savedListingIds.includes(listing.id),
        savedListingIds,
        isLoggedIn,
    };
};
SaveAndShareContainer.propTypes = {
    /**
     * listing data
     */
    listing: PropTypes.object,
    /**
     * Boolean indicating if the current listing is saved
     */
    isSaved: PropTypes.bool,
    /**
     * Boolean indicating if user is logged in
     */
    isLoggedIn: PropTypes.bool,
    /**
     * Child elements to be rendered
     */
    children: PropTypes.node,
};

export default connect(mapStateToProps)(SaveAndShareContainer);
